import {
  InstrumentoArquivoAnexo,
  InstrumentoAvaliacaoRegistro,
  InstrumentoPeriodoOption
} from '../services/instrumento-avaliacao.service';
import {
  InstrumentoArquivoState,
  InstrumentoCampoAdicionalConfig,
  InstrumentoFormularioState,
  InstrumentoItemEducacional,
  InstrumentoResumoView,
  InstrumentoTurmaOption,
  TipoInstrumentoAvaliacaoId
} from '../models/instrumento-avaliacao.types';
import type { InstrumentoCampoNumerico, PeriodoContexto } from '../components/instrumentos-avaliacao.component';
import { normalizarValorCampoAdicionalDecimal } from '../../../utils/componentes/cpa-grid-bridge/cpa-grid-bridge-decimal-normalizer';

interface InstrumentoFormularioContexto {
  turmaId?: number | null;
  itemEducacionalId?: number | null;
  periodoAvaliativoId?: number | null;
  hojeIso?: string;
}

export function createInstrumentoFormulario(contexto: InstrumentoFormularioContexto = {}): InstrumentoFormularioState {
  return {
    version: null,
    versao: null,
    turmaId: contexto.turmaId || null,
    itemEducacionalId: contexto.itemEducacionalId || null,
    periodoAvaliativoId: contexto.periodoAvaliativoId || null,
    tipoInstrumento: null,
    data: '',
    descricao: '',
    sigla: '',
    conteudo: '',
    criteriosAvaliativos: '',
    instrumentoRecuperacaoId: null,
    notaMinima: '',
    notaMaxima: '',
    peso: ''
  };
}

export function mapInstrumentoToForm(
  registro: InstrumentoAvaliacaoRegistro,
  contexto: InstrumentoFormularioContexto = {}
): InstrumentoFormularioState {
  const registroVersionRaw = (registro as { version?: number | string | null; versao?: number | string | null }).version
    ?? (registro as { versao?: number | string | null }).versao;
  const registroVersion = registroVersionRaw === undefined || registroVersionRaw === null || registroVersionRaw === ''
    ? null
    : Number(registroVersionRaw);

  return {
    id: registro.id,
    version: Number.isNaN(registroVersion) ? null : registroVersion,
    versao: Number.isNaN(registroVersion) ? null : registroVersion,
    turmaId: registro.turma?.id || contexto.turmaId || null,
    itemEducacionalId: registro.itemEducacional?.id || contexto.itemEducacionalId || null,
    periodoAvaliativoId: registro.periodoAvaliativo?.id || contexto.periodoAvaliativoId || null,
    tipoInstrumento: (registro.tipoInstrumento as TipoInstrumentoAvaliacaoId) || 'INSTRUMENTO_AVALIACAO',
    data: normalizeDateToInput(registro.data),
    descricao: registro.descricao || '',
    sigla: registro.sigla || '',
    conteudo: String(registro.conteudo || ''),
    criteriosAvaliativos: String(registro.criteriosAvaliativos || ''),
    instrumentoRecuperacaoId: (registro.instrumentoRecuperacao?.id as number) || null,
    notaMinima: registro.notaMinima === null || registro.notaMinima === undefined ? '' : String(registro.notaMinima),
    notaMaxima: registro.notaMaxima === null || registro.notaMaxima === undefined ? '' : String(registro.notaMaxima),
    peso: registro.peso === null || registro.peso === undefined ? '' : String(registro.peso),
    modoAvaliacao: registro.modoAvaliacao || undefined
  };
}

export function mapInstrumentoArquivos(arquivos: InstrumentoArquivoAnexo[] | null | undefined): InstrumentoArquivoState[] {
  return (arquivos || []).map((arquivo) => mapInstrumentoArquivo(arquivo));
}

export function mapInstrumentoArquivo(
  arquivo: InstrumentoArquivoAnexo,
  fallbackNome?: string,
  fallbackTamanho?: number,
  fallbackTipo?: string
): InstrumentoArquivoState {
  return {
    id: arquivo.id,
    nome: String(arquivo.nome || fallbackNome || 'Arquivo sem nome'),
    tipo: String(arquivo.tipo || fallbackTipo || ''),
    tamanho: Number(arquivo.tamanho ?? fallbackTamanho ?? 0) || 0,
    raw: { ...arquivo }
  };
}

export function resolveInstrumentoArquivoIdentificador(anexo: InstrumentoArquivoState, index: number): number | string {
  return anexo.id ?? `instrumento-anexo-${index}`;
}

export function toNullableNumber(value: number | string | null): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  return null;
}

export function formatarInstrumentoTamanhoArquivo(size?: number): string {
  if (!size || size <= 0) {
    return '0 KB';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  let sizeValue = size;
  let unitIndex = 0;

  while (sizeValue >= 1024 && unitIndex < units.length - 1) {
    sizeValue /= 1024;
    unitIndex += 1;
  }

  return `${sizeValue.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

export function buildInstrumentoAnexosAccept(extensoes: readonly string[]): string {
  return extensoes.map((extensao) => `.${extensao}`).join(',');
}

export function buildInstrumentoAnexosLabel(extensoes: readonly string[]): string {
  return `${extensoes.map((extensao) => extensao.toUpperCase()).join(', ')}.`;
}

export function serializeInstrumentoForm(formulario: InstrumentoFormularioState): string {
  return JSON.stringify(formulario);
}

export function serializeInstrumentoArquivos(arquivos: InstrumentoArquivoState[]): string {
  return JSON.stringify(
    arquivos.map((arquivo) => ({
      id: arquivo.id ?? null,
      nome: arquivo.nome,
      tamanho: arquivo.tamanho ?? null,
      tipo: arquivo.tipo ?? ''
    }))
  );
}

export function serializeInstrumentoCampoAdicional(campoAdicional: InstrumentoCampoAdicionalConfig): string {
  return JSON.stringify(campoAdicional ?? null);
}

export function cloneInstrumentoCampoAdicional<T>(value: T): T {
  return value === null || value === undefined
    ? value
    : JSON.parse(JSON.stringify(value)) as T;
}

export function parseOptionalNumber(value: string): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const normalized = Number(String(value).replace(',', '.'));
  return Number.isNaN(normalized) ? null : normalized;
}

export function parseUnknownNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }

  if (typeof value === 'string') {
    return parseOptionalNumber(value);
  }

  return null;
}

export function normalizeDateToInput(value: unknown): string {
  if (!value || typeof value !== 'string') {
    return '';
  }

  return value.includes('T') ? value.split('T')[0] : value;
}

export function parseDate(value: unknown): Date | null {
  if (!value || typeof value !== 'string') {
    return null;
  }

  const normalized = value.includes('T') ? value : `${value}T00:00:00`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getHojeISO(): string {
  const hoje = new Date();
  const offset = hoje.getTimezoneOffset();
  const local = new Date(hoje.getTime() - offset * 60000);
  return local.toISOString().split('T')[0];
}

export function getInstrumentoAnexoExtensao(fileName: string): string {
  const partes = String(fileName || '').toLowerCase().split('.');
  return partes.length > 1 ? partes.pop() || '' : '';
}

/**
 * Remove propriedades internas (ex: `$$hashKey` do AngularJS), normaliza
 * valores de campos adicionais e limpa id/origem quando for um novo registro.
 * Deve ser chamada antes de enviar o payload ao backend.
 */
export function normalizarCampoAdicionalParaPayload(
  value: unknown,
  isNovoRegistro: boolean
): Array<Record<string, unknown>> {
  if (!value) {
    return [];
  }

  const lista = Array.isArray(value)
    ? value
    : (typeof value === 'object' ? [value] : []);

  if (!lista.length) {
    return [];
  }

  const sanitize = (input: unknown): unknown => {
    if (Array.isArray(input)) {
      return input.map((item) => sanitize(item));
    }

    if (input && typeof input === 'object') {
      const clone: Record<string, unknown> = {};
      Object.keys(input as object).forEach((key) => {
        if (key === '$$hashKey') {
          return;
        }
        clone[key] = sanitize((input as Record<string, unknown>)[key]);
      });
      return clone;
    }

    return input;
  };

  let payload = sanitize(lista) as Array<Record<string, unknown>>;
  payload = payload.map((item) => normalizarConfiguracaoCamposAdicionais(item));

  if (isNovoRegistro) {
    payload = payload.map((item) => ({
      ...item,
      id: null,
      origem: null
    }));
  }

  return payload;
}

function normalizarConfiguracaoCamposAdicionais(
  configuracao: Record<string, unknown>
): Record<string, unknown> {
  const agrupadores = Array.isArray(configuracao['agrupadores'])
    ? (configuracao['agrupadores'] as Array<Record<string, unknown>>)
    : [];

  if (!agrupadores.length) {
    return configuracao;
  }

  const agrupadoresNormalizados = agrupadores.map((agrupador) => {
    const campos = Array.isArray(agrupador['campos'])
      ? (agrupador['campos'] as Array<Record<string, unknown>>)
      : [];
    return {
      ...agrupador,
      campos: campos.map(normalizarValorCampoAdicional)
    };
  });

  return {
    ...configuracao,
    agrupadores: agrupadoresNormalizados
  };
}

function normalizarValorCampoAdicional(
  campo: Record<string, unknown>
): Record<string, unknown> {
  const formato = String(campo['formato'] || '').toUpperCase();
  const valor = campo['valor'];

  if (formato === 'INTEGER') {
    const normalizado = String(valor ?? '').replace(/[^\d-]/g, '');
    return {
      ...campo,
      valor: normalizado ? normalizado : null
    };
  }

  return normalizarValorCampoAdicionalDecimal(campo);
}

/**
 * Monta uma chave determinística a partir do contexto (turma/estabelecimento/
 * ano letivo/componentes) para detectar se o contexto realmente mudou entre
 * duas emissões de `mobileContextService.contexto$`.
 */
export function buildContextKey(contexto: unknown): string {
  const value = contexto as {
    turma?: { id?: number };
    estabelecimento?: { id?: number };
    anoLetivo?: { id?: number };
    componentes?: Array<{ id?: number }>;
  } | null;
  const componentesKey = (value?.componentes || [])
    .map((componente) => Number(componente?.id || 0))
    .filter((id) => id > 0)
    .sort((a, b) => a - b)
    .join(',');

  return [value?.turma?.id || 0, value?.estabelecimento?.id || 0, value?.anoLetivo?.id || 0, componentesKey].join(':');
}

/**
 * Indica se a turma é EJA modular, considerando o flag da etapa da matriz,
 * a modalidade ou o texto da descrição/nome da turma.
 */
export function isTurmaEjaModular(turma?: {
  modalidade?: string;
  etapaMatriz?: { modular?: boolean };
  descricao?: string;
  nome?: string;
} | null): boolean {
  const modalidade = String(turma?.modalidade || '').toUpperCase();
  const descricao = String(turma?.descricao || turma?.nome || '').toUpperCase();
  return Boolean(turma?.etapaMatriz?.modular)
    || modalidade === 'EJA_MODULAR'
    || descricao.includes('EJA MODULAR');
}

export function extractTurmaIdFromRaw(raw: Record<string, any>): number | null {
  const turma = raw['turma'] as { id?: number | string } | undefined;
  const turmaId = Number(turma?.id || 0);
  return turmaId > 0 ? turmaId : null;
}

export function getTipoInstrumentoLabel(tipo: TipoInstrumentoAvaliacaoId | string | null | undefined): string {
  switch (tipo) {
    case 'RECUPERACAO_PARALELA':
      return 'Recuperação paralela';
    case 'RECUPERACAO_PERIODO_AVALIATIVO':
      return 'Recuperação do período';
    case 'PONTO_EXTRA':
      return 'Nota complementar';
    default:
      return 'Instrumento de avaliação';
  }
}

export function formatarDataHoraPublicacao(dataPublicacao: string): string {
  const data = new Date(dataPublicacao);
  if (Number.isNaN(data.getTime())) {
    return dataPublicacao;
  }
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function hasInstrumentoCampoAdicionalConteudo(value: InstrumentoCampoAdicionalConfig): boolean {
  if (!value) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
}

export function isPeriodoEncerradoComLista(periodo: PeriodoContexto | null, encerramentos: Array<Record<string, unknown>>): boolean {
  if (!periodo || !encerramentos.length) {
    return false;
  }

  return encerramentos.some((enc) => {
    const encAny = enc as any;
    const status = String(encAny.status || '');
    if (status !== 'ENCERRADO') {
      return false;
    }
    const periodoAvaliativo = encAny.periodoAvaliativo as { id?: number } | number | undefined;
    return (typeof periodoAvaliativo === 'object' && periodoAvaliativo?.id === periodo.id) ||
      (typeof periodoAvaliativo === 'number' && periodoAvaliativo === periodo.id) ||
      encAny.periodoId === periodo.id;
  });
}

export function isPeriodoLetivoEncerrado(encerramentos: Array<Record<string, unknown>>): boolean {
  return encerramentos.some((enc) => {
    const encAny = enc as any;
    const status = String(encAny.status || '');
    const tipoPeriodo = String(encAny.tipoPeriodo || '');
    return status === 'ENCERRADO' && ((!encAny.periodoAvaliativo && !encAny.periodoId) || tipoPeriodo === 'MODULO' || tipoPeriodo === 'PERIODO_LETIVO' || tipoPeriodo === 'ANO');
  });
}

/**
 * Aplica o resultado de uma publicação/despublicação a um registro específico
 * de uma lista, preservando os demais registros intactos (não muta o array recebido).
 */
export function aplicarPublicacaoInstrumento(
  registro: InstrumentoAvaliacaoRegistro,
  instrumentoId: number,
  publicado: boolean,
  dataPublicacao: string | null
): InstrumentoAvaliacaoRegistro {
  if (Number(registro.id || 0) !== instrumentoId) {
    return registro;
  }

  return {
    ...registro,
    publicavel: {
      ...(registro.publicavel || {}),
      publicado,
      dataPublicacao
    },
    publicado,
    publicar: publicado,
    dataPublicacao
  };
}

/**
 * Busca a recuperação paralela vinculada a um instrumento, preferindo o resumo
 * do contexto atual e caindo pro resumo da tela quando não encontrada lá.
 */
export function encontrarRecuperacaoParalelaVinculada(
  contextoResumo: InstrumentoResumoView[],
  telaResumo: InstrumentoResumoView[],
  instrumentoId: number | null
): InstrumentoResumoView | undefined {
  if (!instrumentoId) {
    return undefined;
  }

  return contextoResumo.find((item) => item.tipoInstrumento === 'RECUPERACAO_PARALELA' && item.instrumentoRecuperacaoId === instrumentoId)
    || telaResumo.find((item) => item.tipoInstrumento === 'RECUPERACAO_PARALELA' && item.instrumentoRecuperacaoId === instrumentoId);
}

/**
 * Contexto pra `validarInstrumentoFormulario`. Partes que dependem de serviço
 * (dia letivo) ou de estado entrelaçado do componente devem ser resolvidas
 * ANTES de montar este objeto.
 */
export interface ValidarInstrumentoFormularioContexto {
  labelItensEducacionais: string;
  shouldShowInstrumentoPeriodoField: boolean;
  instrumentoTurmaEjaModular: boolean;
  instrumentoTurmaEAtividade: boolean;
  instrumentoExibeFaixaNotas: boolean;
  instrumentoExibePeso: boolean;
  instrumentoPesoMinimoLimite: number;
  instrumentoPesoMaximoLimite: number;
  instrumentoConfiguracaoTurma: unknown;
  instrumentoConfiguracaoEstabelecimento: unknown;
  instrumentoContextoResumo: InstrumentoResumoView[];
  instrumentosRecuperacaoOptions: InstrumentoResumoView[];
  rangePeriodoSelecionado: { inicio: Date; fim: Date } | null;
  diaLetivoValido: boolean;
  alteracaoManualPeriodoAvaliativo: boolean;
  anoLetivoAtual: number | null;
  alteracoesBloqueadasPorRecuperacaoVinculada: string[];
  instrumentoCamposAdicionaisObrigatoriosValidos: boolean;
}

export function validarInstrumentoFormulario(
  form: InstrumentoFormularioState,
  contexto: ValidarInstrumentoFormularioContexto
): string[] {
  const erros: string[] = [];
  const descricao = form.descricao.trim();
  const sigla = form.sigla.trim();
  const tipoInstrumento = form.tipoInstrumento;
  const data = parseDate(form.data);
  const notaMinima = parseOptionalNumber(form.notaMinima);
  const notaMaxima = parseOptionalNumber(form.notaMaxima);
  const peso = parseOptionalNumber(form.peso);

  if (!form.turmaId) erros.push('Informe a turma.');
  if (!form.itemEducacionalId) erros.push(`Informe o ${contexto.labelItensEducacionais}.`);
  if (contexto.shouldShowInstrumentoPeriodoField && !form.periodoAvaliativoId) erros.push('Informe o período avaliativo.');
  if (!form.data) erros.push('Informe a data do instrumento.');
  if (!tipoInstrumento) erros.push('Informe o tipo de instrumento de avaliação.');
  if (!descricao) erros.push('Informe a descrição do instrumento.');
  if (!sigla) erros.push('Informe a sigla do instrumento.');
  if (sigla.length > 5) erros.push('A sigla do instrumento deve ter no máximo 5 caracteres.');
  // Duplicidade é validada apenas no back-end
  if (tipoInstrumento === 'RECUPERACAO_PARALELA' && !form.instrumentoRecuperacaoId) erros.push('Selecione o instrumento de avaliação a ser recuperado.');
  if (contexto.instrumentoTurmaEjaModular) erros.push('Não é permitido cadastrar instrumentos de avaliação para turmas de EJA modular.');

  if (tipoInstrumento === 'RECUPERACAO_PARALELA' && form.instrumentoRecuperacaoId) {
    const recuperacaoExistente = contexto.instrumentoContextoResumo.find((item) =>
      item.id !== form.id &&
      item.tipoInstrumento === 'RECUPERACAO_PARALELA' &&
      item.instrumentoRecuperacaoId === form.instrumentoRecuperacaoId
    );
    if (recuperacaoExistente) erros.push('Cada instrumento de avaliação pode possuir apenas uma recuperação paralela.');
  }

  if (tipoInstrumento === 'RECUPERACAO_PERIODO_AVALIATIVO') {
    if (contexto.instrumentoTurmaEAtividade || contexto.instrumentoTurmaEjaModular) {
      erros.push('Recuperação do período avaliativo não está disponível para este tipo de turma.');
    }

    const recuperacaoPeriodoExistente = contexto.instrumentoContextoResumo.find((item) =>
      item.id !== form.id && item.tipoInstrumento === 'RECUPERACAO_PERIODO_AVALIATIVO'
    );
    if (recuperacaoPeriodoExistente) erros.push('Já existe uma recuperação do período avaliativo cadastrada para este contexto.');
  }

  if (contexto.instrumentoExibeFaixaNotas && ((notaMinima !== null && notaMaxima === null) || (notaMinima === null && notaMaxima !== null))) {
    erros.push('Informe a menor e a maior nota do instrumento de avaliação.');
  }

  if (contexto.instrumentoExibeFaixaNotas && notaMinima !== null && notaMaxima !== null && notaMinima > notaMaxima) {
    erros.push('A maior nota deve ser maior ou igual à menor nota.');
  }

  const notaMinimaConfiguracao = (contexto.instrumentoConfiguracaoTurma as { notaMinima?: number } | null)?.notaMinima
    ?? (contexto.instrumentoConfiguracaoEstabelecimento as { notaMinima?: number } | null)?.notaMinima
    ?? 0;
  const notaMaximaConfiguracao = (contexto.instrumentoConfiguracaoTurma as { notaMaxima?: number } | null)?.notaMaxima
    ?? (contexto.instrumentoConfiguracaoEstabelecimento as { notaMaxima?: number } | null)?.notaMaxima
    ?? 10;

  if (contexto.instrumentoExibeFaixaNotas && notaMinima !== null && notaMinima < 0) {
    erros.push('A menor nota não pode ser negativa.');
  }

  if (contexto.instrumentoExibeFaixaNotas && notaMaxima !== null && notaMaxima < 0) {
    erros.push('A maior nota não pode ser negativa.');
  }

  if (contexto.instrumentoExibePeso && peso !== null && peso < 0) {
    erros.push('O peso não pode ser negativo.');
  }

  if (contexto.instrumentoExibeFaixaNotas && notaMinima !== null && !Number.isNaN(notaMinimaConfiguracao) && notaMinima < notaMinimaConfiguracao) {
    erros.push(`A menor nota deve ser maior ou igual a ${notaMinimaConfiguracao}.`);
  }

  if (contexto.instrumentoExibeFaixaNotas && notaMaxima !== null && !Number.isNaN(notaMaximaConfiguracao) && notaMaxima > notaMaximaConfiguracao) {
    erros.push(`A maior nota deve ser menor ou igual a ${notaMaximaConfiguracao}.`);
  }

  if (contexto.instrumentoExibePeso && peso !== null && peso < contexto.instrumentoPesoMinimoLimite) {
    erros.push(`O peso deve ser maior ou igual a ${contexto.instrumentoPesoMinimoLimite}.`);
  }

  if (contexto.instrumentoExibePeso && peso !== null && peso > contexto.instrumentoPesoMaximoLimite) {
    erros.push(`O peso deve ser menor ou igual a ${contexto.instrumentoPesoMaximoLimite}.`);
  }

  if (data && contexto.shouldShowInstrumentoPeriodoField) {
    const range = contexto.rangePeriodoSelecionado;
    if ((tipoInstrumento === 'INSTRUMENTO_AVALIACAO' || tipoInstrumento === 'PONTO_EXTRA') && range && (data < range.inicio || data > range.fim)) {
      erros.push('A data do instrumento deve pertencer ao período avaliativo selecionado.');
    }
  }

  if (data && !contexto.diaLetivoValido) {
    erros.push('A data informada deve ser um dia letivo para a turma selecionada.');
  }

  if (data && tipoInstrumento === 'RECUPERACAO_PARALELA') {
    const instrumentoRecuperacao = contexto.instrumentosRecuperacaoOptions.find((item) => item.id === form.instrumentoRecuperacaoId);
    const dataRecuperacao = parseDate(instrumentoRecuperacao?.data);
    if (dataRecuperacao && data <= dataRecuperacao) {
      erros.push('A data da recuperação paralela deve ser maior que a data do instrumento recuperado.');
    }
  }

  if (data && tipoInstrumento === 'RECUPERACAO_PERIODO_AVALIATIVO') {
    const datasInstrumentos = contexto.instrumentoContextoResumo
      .filter((item) => item.tipoInstrumento === 'INSTRUMENTO_AVALIACAO')
      .map((item) => parseDate(item.data))
      .filter((item): item is Date => !!item)
      .sort((a, b) => b.getTime() - a.getTime());

    if (datasInstrumentos.length && data <= datasInstrumentos[0]) {
      erros.push('A data da recuperação do período avaliativo deve ser maior que a data dos instrumentos do período.');
    }
  }

  if (form.id && tipoInstrumento === 'RECUPERACAO_PARALELA' && contexto.alteracaoManualPeriodoAvaliativo) {
    erros.push('O período avaliativo de uma recuperação paralela não pode ser alterado manualmente.');
  }

  if (data && tipoInstrumento === 'RECUPERACAO_PERIODO_AVALIATIVO') {
    if (contexto.anoLetivoAtual !== null && data.getFullYear() !== contexto.anoLetivoAtual) {
      erros.push('A data da recuperação do período avaliativo deve estar dentro do ano letivo selecionado.');
    }
  }

  if (form.id && contexto.alteracoesBloqueadasPorRecuperacaoVinculada.length) {
    erros.push(`Existe uma recuperação paralela vinculada. Não é permitido alterar: ${contexto.alteracoesBloqueadasPorRecuperacaoVinculada.join(', ')}.`);
  }

  if (!contexto.instrumentoCamposAdicionaisObrigatoriosValidos) {
    erros.push('Preencha os campos obrigatórios em dados adicionais.');
  }

  return Array.from(new Set(erros));
}

/**
 * Contexto pra montar o payload do instrumento. Valores que dependem de estado
 * entrelaçado do componente devem ser resolvidos ANTES de chamar esta função;
 * a normalização de recuperação paralela (`normalizarPayloadRecuperacaoParalela`)
 * é aplicada pelo componente DEPOIS.
 */
export interface ConstruirInstrumentoPayloadContexto {
  periodoDetalhado: InstrumentoPeriodoOption | undefined;
  instrumentoRecuperacao: InstrumentoResumoView | null;
  instrumentoRecuperacaoDetalhado: InstrumentoAvaliacaoRegistro | null | undefined;
  modoAvaliacaoAtual: string | undefined;
  turmaSelecionada: InstrumentoTurmaOption | null;
  itemSelecionado: InstrumentoItemEducacional | null;
  shouldShowInstrumentoPeriodoField: boolean;
  instrumentoPeriodoSelecionado: PeriodoContexto | null;
  instrumentoRegistroOriginal: InstrumentoAvaliacaoRegistro | null;
  instrumentoTurmaEAtividade: boolean;
  instrumentoPeriodos: InstrumentoPeriodoOption[];
  instrumentoExibeFaixaNotas: boolean;
  instrumentoExibePeso: boolean;
  instrumentoArquivos: InstrumentoArquivoState[];
  instrumentoCampoAdicional: InstrumentoCampoAdicionalConfig;
}

export function construirInstrumentoPayload(
  form: InstrumentoFormularioState,
  contexto: ConstruirInstrumentoPayloadContexto
): InstrumentoAvaliacaoRegistro {
  const modoAvaliacao = contexto.instrumentoRecuperacao?.modoAvaliacao || contexto.modoAvaliacaoAtual;
  const periodoSelecionado = contexto.shouldShowInstrumentoPeriodoField
    ? ((contexto.periodoDetalhado?.periodoAvaliativo as { id: number; descricao?: string; dataInicial?: string; dataFinal?: string }) || contexto.instrumentoPeriodoSelecionado || null)
    : null;
  const payloadBase = form.id && contexto.instrumentoRegistroOriginal
    ? JSON.parse(JSON.stringify(contexto.instrumentoRegistroOriginal)) as InstrumentoAvaliacaoRegistro
    : {} as InstrumentoAvaliacaoRegistro;
  const version = form.version ?? form.versao ?? undefined;

  delete (payloadBase as any).publicavel;
  delete (payloadBase as any).publicado;
  delete (payloadBase as any).dataPublicacao;
  delete (payloadBase as any).versao;

  payloadBase.id = form.id || undefined;
  if (version === undefined || version === null) {
    delete (payloadBase as any).version;
  } else {
    payloadBase.version = version;
  }

  payloadBase.turma = contexto.turmaSelecionada ? { ...(payloadBase.turma || {}), ...contexto.turmaSelecionada } : payloadBase.turma;
  payloadBase.itemEducacional = contexto.itemSelecionado ? { ...(payloadBase.itemEducacional || {}), ...contexto.itemSelecionado } : payloadBase.itemEducacional;
  payloadBase.periodoAvaliativo = periodoSelecionado
    ? { ...((payloadBase.periodoAvaliativo as Record<string, unknown> | null) || {}), ...periodoSelecionado } as any
    : null;
  payloadBase.tipoInstrumento = form.tipoInstrumento || payloadBase.tipoInstrumento || undefined;
  payloadBase.tipoPeriodo = contexto.instrumentoTurmaEAtividade
    ? String(contexto.instrumentoPeriodos[0]?.tipoPeriodo || payloadBase.tipoPeriodo || 'INSTRUMENTO')
    : String(contexto.periodoDetalhado?.tipoPeriodo || payloadBase.tipoPeriodo || 'INSTRUMENTO');
  (payloadBase as any).tipo = String((payloadBase as any).tipo || 'INSTRUMENTO');
  payloadBase.modoAvaliacao = modoAvaliacao || payloadBase.modoAvaliacao || undefined;
  payloadBase.data = form.data;
  payloadBase.descricao = form.descricao.trim();
  payloadBase.sigla = form.sigla.trim();
  payloadBase.conteudo = form.conteudo?.trim() || '';
  payloadBase.criteriosAvaliativos = form.criteriosAvaliativos?.trim() || '';
  payloadBase.instrumentoRecuperacao = contexto.instrumentoRecuperacao
    ? {
        ...((((payloadBase as any).instrumentoRecuperacao as Record<string, unknown> | null) || {})),
        ...(contexto.instrumentoRecuperacaoDetalhado || contexto.instrumentoRecuperacao.raw)
      } as any
    : null;
  payloadBase.notaMinima = contexto.instrumentoExibeFaixaNotas ? parseOptionalNumber(form.notaMinima) : undefined;
  payloadBase.notaMaxima = contexto.instrumentoExibeFaixaNotas ? parseOptionalNumber(form.notaMaxima) : undefined;
  payloadBase.peso = contexto.instrumentoExibePeso ? parseOptionalNumber(form.peso) : undefined;
  (payloadBase as any).arquivos = contexto.instrumentoArquivos.map((arquivo) => ({ ...arquivo.raw }));
  const campoAdicionalBruto = contexto.instrumentoCampoAdicional
    ? cloneInstrumentoCampoAdicional(contexto.instrumentoCampoAdicional)
    : (Array.isArray((payloadBase as any).campoAdicional) ? (payloadBase as any).campoAdicional : []);
  (payloadBase as any).campoAdicional = normalizarCampoAdicionalParaPayload(campoAdicionalBruto, !payloadBase.id);
  (payloadBase as any).competenciaCHA = Array.isArray((payloadBase as any).competenciaCHA) ? (payloadBase as any).competenciaCHA : [];
  (payloadBase as any).objetivoAprendizagem = Array.isArray((payloadBase as any).objetivoAprendizagem) ? (payloadBase as any).objetivoAprendizagem : [];

  return payloadBase;
}

/**
 * Sanitiza a digitação de um campo numérico (notaMinima/notaMaxima/peso):
 * remove caracteres inválidos, limita dígitos e aplica os limites de negócio
 * (peso máximo fixo, nota máxima configurável por turma/estabelecimento).
 */
export function sanitizeInstrumentoCampoNumerico(
  value: unknown,
  campo: InstrumentoCampoNumerico | undefined,
  instrumentoPesoMaximoLimite: number,
  instrumentoConfiguracaoTurma: unknown
): string {
  const raw = String(value ?? '').replace(',', '.');
  let semCaracteresInvalidos = raw.replace(/[^\d.]/g, '');

  // Limitar a parte inteira a 2 dígitos para notaMinima/notaMaxima
  if (campo === 'notaMinima' || campo === 'notaMaxima') {
    const partes = semCaracteresInvalidos.split('.');
    let parteInteira = partes[0] || '';
    if (parteInteira.length > 2) {
      parteInteira = parteInteira.slice(0, 2);
    }
    const parteDecimal = partes.length > 1 ? '.' + partes.slice(1).join('') : '';
    semCaracteresInvalidos = parteInteira + parteDecimal;
  }

  const partes = semCaracteresInvalidos.split('.');
  const parteInteira = partes.shift() || '';
  const parteDecimal = partes.join('');
  const sanitizado = parteDecimal ? `${parteInteira}.${parteDecimal}` : parteInteira;

  // Validações adicionais baseadas no campo
  if (campo === 'peso') {
    const [inteiroPeso = '', decimalPeso = ''] = sanitizado.split('.');
    const inteiroLimitado = inteiroPeso.slice(0, 3);
    const pesoSanitizado = decimalPeso ? `${inteiroLimitado}.${decimalPeso}` : inteiroLimitado;
    const numero = parseOptionalNumber(pesoSanitizado);

    if (numero !== null && numero <= 0) {
      return '';
    }

    if (numero !== null && numero > instrumentoPesoMaximoLimite) {
      return String(instrumentoPesoMaximoLimite);
    }

    return pesoSanitizado;
  } else if (campo === 'notaMinima' || campo === 'notaMaxima') {
    const numero = parseOptionalNumber(sanitizado);
    if (numero !== null && numero < 0) {
      return '';
    }
    const notaMaximaConfiguracao = Number((instrumentoConfiguracaoTurma as { notaMaxima?: number } | null)?.notaMaxima);
    if (campo === 'notaMaxima' && numero !== null && !Number.isNaN(notaMaximaConfiguracao) && numero > notaMaximaConfiguracao) {
      return String(notaMaximaConfiguracao);
    }
  }

  return sanitizado;
}
